/**
 * 师资录入/编辑表单页面
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Card,
  Row,
  Col,
  message,
  Space,
  Typography,
  Upload,
  Divider
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import facultyService, {
  Faculty,
  CreateFacultyInput,
  FacultyCategory,
  CertificateType,
  UploadResult
} from '../services/facultyService';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

interface FacultyFormProps {
  mode?: 'create' | 'edit';
}

const FacultyForm: React.FC<FacultyFormProps> = ({ mode = 'create' }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<FacultyCategory[]>([]);
  const [certificateTypes, setCertificateTypes] = useState<CertificateType[]>([]);
  const [filteredCertificates, setFilteredCertificates] = useState<CertificateType[]>([]);

  // 文件上传状态
  const [uploadingFiles, setUploadingFiles] = useState<{
    id_card: boolean;
    title_cert: boolean;
    qualification_cert: boolean;
  }>({
    id_card: false,
    title_cert: false,
    qualification_cert: false
  });

  const isEdit = mode === 'edit' && id;

  /**
   * 加载师资类别
   */
  const loadFacultyCategories = async () => {
    try {
      const data = await facultyService.getFacultyCategories();
      setCategories(data);
    } catch (error) {
      message.error('加载师资类别失败');
    }
  };

  /**
   * 加载证书类别
   */
  const loadCertificateTypes = async () => {
    try {
      const data = await facultyService.getCertificateTypes();
      setCertificateTypes(data);
    } catch (error) {
      message.error('加载证书类别失败');
    }
  };

  /**
   * 加载师资详情（编辑模式）
   */
  const loadFacultyDetail = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const faculty = await facultyService.getFacultyById(parseInt(id));
      form.setFieldsValue({
        ...faculty,
        daily_fee: faculty.daily_fee || undefined
      });

      // 加载对应的证书级别选项
      if (faculty.certificate_category) {
        const filtered = certificateTypes.filter(cert => cert.category === faculty.certificate_category);
        setFilteredCertificates(filtered);
      }
    } catch (error) {
      message.error('加载师资详情失败');
      navigate('/faculty');
    } finally {
      setLoading(false);
    }
  }, [id, form, navigate, certificateTypes]);

  useEffect(() => {
    const initializeForm = async () => {
      await loadFacultyCategories();
      await loadCertificateTypes();
    };

    initializeForm();
  }, []);

  useEffect(() => {
    if (isEdit && certificateTypes.length > 0) {
      loadFacultyDetail();
    }
  }, [isEdit, loadFacultyDetail, certificateTypes.length]);

  /**
   * 证书类别变化时过滤级别选项
   */
  const handleCertificateCategoryChange = (category: string) => {
    const filtered = certificateTypes.filter(cert => cert.category === category);
    setFilteredCertificates(filtered);

    // 清空证书级别选择
    form.setFieldsValue({ certificate_level: undefined });
  };

  /**
   * 处理文件上传
   */
  const handleFileUpload = async (
    file: File,
    fieldName: 'id_card' | 'title_cert' | 'qualification_cert'
  ): Promise<boolean> => {
    const pathFieldName = `${fieldName}_file_path` as keyof CreateFacultyInput;

    try {
      setUploadingFiles(prev => ({ ...prev, [fieldName]: true }));

      const result: UploadResult = await facultyService.uploadFile(file, 'faculty');

      // 更新表单字段值
      form.setFieldsValue({
        [pathFieldName]: result.url
      });

      message.success('文件上传成功');
      return true;

    } catch (error: any) {
      message.error(error.message || '文件上传失败');
      return false;
    } finally {
      setUploadingFiles(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  /**
   * 自定义上传处理
   */
  const customUpload = (fieldName: 'id_card' | 'title_cert' | 'qualification_cert') => ({
    beforeUpload: async (file: File) => {
      await handleFileUpload(file, fieldName);
      return false; // 阻止默认上传行为
    },
    showUploadList: false, // 不显示上传列表
  });

  /**
   * 提交表单
   */
  const handleSubmit = async (values: CreateFacultyInput) => {
    setLoading(true);
    try {
      if (isEdit) {
        await facultyService.updateFaculty(parseInt(id!), values);
        message.success('更新师资信息成功');
      } else {
        await facultyService.createFaculty(values);
        message.success('创建师资信息成功');
      }
      navigate('/faculty');
    } catch (error: any) {
      message.error(error.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 返回列表
   */
  const handleBack = () => {
    navigate('/faculty');
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col flex="auto">
              <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                  返回
                </Button>
                <Title level={3} style={{ margin: 0 }}>
                  {isEdit ? '编辑师资信息' : '新增师资信息'}
                </Title>
              </Space>
            </Col>
          </Row>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            gender: '男'
          }}
        >
          <Row gutter={24}>
            {/* 基本信息 */}
            <Col span={24}>
              <Title level={4}>基本信息</Title>
            </Col>

            <Col span={8}>
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input placeholder="请输入姓名" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="gender"
                label="性别"
                rules={[{ required: true, message: '请选择性别' }]}
              >
                <Select placeholder="请选择性别">
                  <Option value="男">男</Option>
                  <Option value="女">女</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="category"
                label="师资类别"
                rules={[{ required: true, message: '请选择师资类别' }]}
              >
                <Select placeholder="请选择师资类别">
                  {categories.map(cat => (
                    <Option key={cat.id} value={cat.name}>{cat.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="region"
                label="地区"
                rules={[{ required: true, message: '请输入地区' }]}
              >
                <Input placeholder="请输入地区" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="residence"
                label="常驻地"
              >
                <Input placeholder="请输入常驻地" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="phone"
                label="联系电话"
                rules={[
                  { required: true, message: '联系电话为必填项' },
                  { pattern: /^(\d{11}|\d{3,4}-\d{7,8})$/, message: '请输入正确的电话号码' }
                ]}
              >
                <Input placeholder="请输入联系电话" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="introduction"
                label="简介"
              >
                <TextArea
                  rows={4}
                  placeholder="请输入师资简介"
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Col>

            {/* 费用和评价 */}
            <Col span={24}>
              <Divider />
              <Title level={4}>费用和评价</Title>
            </Col>

            <Col span={8}>
              <Form.Item
                name="daily_fee"
                label="课酬(元/天)"
                rules={[
                  { type: 'number', min: 0, max: 999999, message: '课酬必须在0-999999之间' }
                ]}
              >
                <InputNumber
                  placeholder="请输入课酬"
                  style={{ width: '100%' }}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/¥\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="local_evaluation"
                label="当地评价"
              >
                <Select placeholder="请选择当地评价" allowClear>
                  <Option value="优秀">优秀</Option>
                  <Option value="非常优秀">非常优秀</Option>
                  <Option value="良好">良好</Option>
                  <Option value="一般">一般</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="remarks"
                label="备注"
              >
                <TextArea
                  rows={3}
                  placeholder="请输入备注信息"
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Col>

            {/* 证书信息 */}
            <Col span={24}>
              <Divider />
              <Title level={4}>证书信息</Title>
            </Col>

            <Col span={8}>
              <Form.Item
                name="certificate_category"
                label="证书类别"
                rules={[{ required: true, message: '请选择证书类别' }]}
              >
                <Select
                  placeholder="请选择证书类别"
                  onChange={handleCertificateCategoryChange}
                >
                  <Option value="创业">创业</Option>
                  <Option value="就业">就业</Option>
                  <Option value="职业技能">职业技能</Option>
                  <Option value="教师证">教师证</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="certificate_level"
                label="证书级别"
                rules={[{ required: true, message: '请选择证书级别' }]}
              >
                <Select
                  placeholder="请选择证书级别"
                  disabled={!form.getFieldValue('certificate_category')}
                >
                  {filteredCertificates.map(cert => (
                    <Option key={cert.id} value={cert.level}>
                      {cert.level} ({cert.organization})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="certificate_name"
                label="证书名称"
                rules={[{ required: true, message: '请输入证书名称' }]}
              >
                <Input placeholder="请输入证书名称" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="teacher_certificate"
                label="教师资格证"
              >
                <Input placeholder="请输入教师资格证信息" />
              </Form.Item>
            </Col>

            {/* 文件上传 */}
            <Col span={24}>
              <Divider />
              <Title level={4}>证件上传</Title>
            </Col>

            <Col span={8}>
              <Form.Item label="身份证扫描件">
                <div>
                  <Upload
                    {...customUpload('id_card')}
                    accept="image/*,.pdf"
                    listType="picture-card"
                    maxCount={1}
                  >
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>
                        {uploadingFiles.id_card ? '上传中...' : '上传身份证'}
                      </div>
                    </div>
                  </Upload>
                  <Form.Item
                    name="id_card_file_path"
                    style={{ marginTop: 8, marginBottom: 0 }}
                    rules={[{ required: true, message: '请上传身份证扫描件' }]}
                  >
                    <Input
                      placeholder="文件URL（上传后自动填充）"
                      size="small"
                      disabled
                      readOnly
                    />
                  </Form.Item>
                </div>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="职称证书扫描件">
                <div>
                  <Upload
                    {...customUpload('title_cert')}
                    accept="image/*,.pdf"
                    listType="picture-card"
                    maxCount={1}
                  >
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>
                        {uploadingFiles.title_cert ? '上传中...' : '上传职称证书'}
                      </div>
                    </div>
                  </Upload>
                  <Form.Item
                    name="title_cert_file_path"
                    style={{ marginTop: 8, marginBottom: 0 }}
                    rules={[{ required: true, message: '请上传职称证书扫描件' }]}
                  >
                    <Input
                      placeholder="文件URL（上传后自动填充）"
                      size="small"
                      disabled
                      readOnly
                    />
                  </Form.Item>
                </div>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="相关资格证书扫描件">
                <div>
                  <Upload
                    {...customUpload('qualification_cert')}
                    accept="image/*,.pdf"
                    listType="picture-card"
                    maxCount={1}
                  >
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>
                        {uploadingFiles.qualification_cert ? '上传中...' : '上传资格证书'}
                      </div>
                    </div>
                  </Upload>
                  <Form.Item
                    name="qualification_cert_file_path"
                    style={{ marginTop: 8, marginBottom: 0 }}
                    rules={[{ required: true, message: '请上传相关资格证书扫描件' }]}
                  >
                    <Input
                      placeholder="文件URL（上传后自动填充）"
                      size="small"
                      disabled
                      readOnly
                    />
                  </Form.Item>
                </div>
              </Form.Item>
            </Col>
          </Row>

          {/* 提交按钮 */}
          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleBack}>
                取消
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                {isEdit ? '更新' : '保存'}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default FacultyForm;